import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { styled } from '@mui/system';
import { Menu, MenuItem } from '@mui/material';
import { DeleteRounded, TimelineRounded, Settings } from '@mui/icons-material';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import DeleteAddressbookDialog from '#component/addressbook/dashboard/delete-addressbook-dialog';
import StopDialog from '#component/warehouse/appliance-overview/stop-dialog';
import { deleteAppliance, stopAppliance } from '#action/warehouse/appliance/appliance';
import { fetchAppliances } from '#action/warehouse/appliance/appliance-paginated';
import { getContrastColor } from '#component/theme';

const StyledMenuItem = styled(MenuItem)({
  minWidth: 160
});

const StyledTimelineIcon = styled(TimelineRounded)(({ theme }) => ({
  color: getContrastColor(),
  marginRight: theme.spacing()
}));

const StyledSettingsIcon = styled(Settings)(({ theme }) => ({
  color: getContrastColor(),
  marginRight: theme.spacing()
}));

const StyledAuditTrailIcon = styled(SpeakerNotesOutlinedIcon)(({ theme }) => ({
  color: getContrastColor(),
  marginRight: theme.spacing()
}));

const StyledStopIcon = styled(StopRoundedIcon)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.54)',
  marginRight: theme.spacing()
}));

const StyledDeleteIcon = styled(DeleteRounded)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.54)',
  marginRight: theme.spacing()
}));

const APPLIANCE_STATUS = {
  STOPPED: 300
};

/**
 * Renders a warehouse appliances actions and delete dialog.
 * @returns {React.FC}
 */

const ApplianceListActions = props => {
  const { anchorEl, setAnchorEl = () => null, appliance, userType } = props;
  const [open, setOpen] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [isLocallyStop, setIsLocallyStop] =
    useState(appliance?.status === APPLIANCE_STATUS.STOPPED);
  const dispatch = useDispatch();

  const isApplianceStop = isLocallyStop || appliance?.status === APPLIANCE_STATUS.STOPPED;

  useEffect(() => {
    setIsLocallyStop(appliance?.status === APPLIANCE_STATUS.STOPPED);
  }, [appliance]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setOpen(true);
  };

  const handleDeleteClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteAppliance(appliance?.id));
    if (result?.success) {
      handleDeleteClose();
      dispatch(fetchAppliances());
    }
  };

  const handleStopClose = () => {
    setShowStopDialog(false);
    setAnchorEl(null);
  };

  const handleStop = async ({ stopDate }) => {
    const result = await dispatch(stopAppliance(appliance?.id, { stopDate }));
    if (result?.success) {
      setIsLocallyStop(true);
      handleStopClose();
      dispatch(fetchAppliances());
    }
  };

  return (
    <>
      {userType <= 20 &&
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {userType < 10 && (
            <StyledMenuItem
              component={Link}
              to={`/warehouse/appliance-overview/${appliance?.id}`}
            >
              <StyledTimelineIcon />
              Timeline
            </StyledMenuItem>
          )}
          {userType < 10 && (
            <StyledMenuItem
              component={Link}
              to={`/warehouse/appliance/edit/${appliance?.id}`}
            >
              <StyledSettingsIcon />
              Settings
            </StyledMenuItem>
          )}
          <StyledMenuItem
            component={Link}
            to={`/warehouse-log?appliance=${appliance?.id}`}
          >
            <StyledAuditTrailIcon />
            Audit trail
          </StyledMenuItem>
          {userType < 10 && !isApplianceStop && (
            <StyledMenuItem
              onClick={() => {
                setAnchorEl(null);
                setShowStopDialog(true);
              }}
            >
              <StyledStopIcon />
              Stop
            </StyledMenuItem>
          )}
          {userType < 10 &&
            <StyledMenuItem onClick={handleDeleteClick}>
              <StyledDeleteIcon />
              Delete
            </StyledMenuItem>}
        </Menu>}
      <DeleteAddressbookDialog
        open={open}
        dialogTitle={`Do you want to delete the "${appliance?.label ?? ''}"?`}
        entityTitle={appliance?.label ?? ''}
        dialogContentText="Type the application label to confirm"
        textFieldLabel="Label"
        onClose={handleDeleteClose}
        onDelete={handleDelete}
      />
      <StopDialog
        appliance={appliance}
        dispatch={dispatch}
        showDialog={showStopDialog}
        setShowDialog={setShowStopDialog}
        onClose={handleStopClose}
        onStop={handleStop}
      />
    </>
  );
};

export default ApplianceListActions;
